"use client";

import { UserIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Toggle } from "@/components/ui/toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WorkspaceSession } from "@/lib/mlx-workspace-client";
import { workspaceSessionOwnedByCurrentUser } from "@/lib/workspace-session-owner";

export type SessionNavigationScope = "everyone" | "mine";

const SCOPE_STORAGE_KEY = "mlx:workspace:navigation-session-scope:v1";

export function sessionsInNavigationScope(
  sessions: WorkspaceSession[],
  scope: SessionNavigationScope,
  currentUserId: string | null,
): WorkspaceSession[] {
  // Without a resolved viewer nothing can be attributed; keep the full list.
  if (scope === "everyone" || !currentUserId) return sessions;
  return sessions.filter((session) =>
    workspaceSessionOwnedByCurrentUser(session, currentUserId),
  );
}

export function useSessionNavigationScope() {
  const [scope, setScope] = useState<SessionNavigationScope>("everyone");

  useEffect(() => {
    try {
      if (window.localStorage.getItem(SCOPE_STORAGE_KEY) === "mine")
        setScope("mine");
    } catch {
      // The default scope remains available when storage is blocked.
    }
  }, []);

  const selectScope = useCallback((next: SessionNavigationScope) => {
    setScope(next);
    try {
      window.localStorage.setItem(SCOPE_STORAGE_KEY, next);
    } catch {
      // Selection still works for this tab when storage is blocked.
    }
  }, []);

  return [scope, selectScope] as const;
}

export function SessionScopeToggle(props: {
  scope: SessionNavigationScope;
  onScopeChange: (scope: SessionNavigationScope) => void;
}) {
  const mine = props.scope === "mine";
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Toggle
            aria-label="Show only my sessions"
            className="ml-1 size-7 min-w-7 shrink-0 px-0 text-muted-foreground aria-pressed:text-foreground"
            onPressedChange={(pressed) =>
              props.onScopeChange(pressed ? "mine" : "everyone")
            }
            pressed={mine}
            size="sm"
          />
        }
      >
        <UserIcon className="size-3.5" strokeWidth={1.75} />
      </TooltipTrigger>
      <TooltipContent side="right">
        {mine ? "Showing my sessions" : "Show only my sessions"}
      </TooltipContent>
    </Tooltip>
  );
}
